'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import { usePathname } from 'next/navigation';

// Pages where the fixed CTA must NOT appear
const EXCLUDED_PATHS = [
  '/soutenir',
  '/faire-un-don',
  '/adhesion',
  '/aide-immediate',
  '/mentions-legales',
  '/confidentialite',
  '/cookies',
  '/conditions',
  '/accessibilite',
  '/connexion-et-r-cup-ration-d-acc-s',
  '/tableau-de-bord',
  '/tre-accompagn', // has its own primary CTA
];

export default function MobileCTA() {
  const pathname = usePathname();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const footerObserverRef = useRef<IntersectionObserver | null>(null);

  const isExcluded = EXCLUDED_PATHS?.some(p => pathname?.startsWith(p));

  // Show after 20-25% scroll
  useEffect(() => {
    if (isExcluded) return;

    // Check session storage for dismissed state
    const sessionDismissed = sessionStorage.getItem('mobileCTA_dismissed');
    if (sessionDismissed === 'true') {
      setDismissed(true);
      return;
    }

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement?.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent >= 20) {
        setVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isExcluded]);

  // Hide when footer is visible
  useEffect(() => {
    if (isExcluded) return;
    const footer = document.querySelector('footer');
    if (!footer) return;

    footerObserverRef.current = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    footerObserverRef?.current?.observe(footer);
    return () => footerObserverRef?.current?.disconnect();
  }, [isExcluded]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('mobileCTA_dismissed', 'true');
  };

  if (isExcluded || dismissed || !visible || footerVisible) return null;

  return (
    <div
      className="fixed z-40 xl:hidden"
      style={{
        left: '12px',
        right: '12px',
        bottom: `calc(12px + env(safe-area-inset-bottom, 0px))`,
        maxWidth: '420px',
        marginInline: 'auto',
      }}
      role="complementary"
      aria-label="Action principale"
    >
      <div
        className="flex items-center gap-2 rounded-xl shadow-lg"
        style={{
          background: '#071923',
          border: '1px solid rgba(255,255,255,0.12)',
          padding: '10px 10px 10px 12px',
          minHeight: '52px',
        }}
      >
        <Link
          href="/tre-accompagn"
          className="flex-1 flex items-center justify-center gap-2 rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D1A338]/70 font-semibold"
          style={{
            background: '#D1A338',
            color: '#071923',
            minHeight: '44px',
            fontSize: '15px',
            padding: '0 16px',
          }}
        >
          Demander un accompagnement
        </Link>
        <button
          onClick={handleDismiss}
          className="flex items-center justify-center rounded-lg transition-all duration-200 flex-shrink-0 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          style={{ width: '40px', height: '44px', color: 'rgba(255,255,255,0.60)' }}
          aria-label="Fermer ce bandeau"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
